import {useApiariesStore} from 'entities/apiaries';
import {useEffect, useState} from 'react';
import {usePageTitleStore} from 'entities/pageTitle';
import {useDevicesStore} from 'entities/devices';
import {useOutletColorStore} from 'entities/outletColor/model/store.ts';
import {
  ArchiveApiaryButton,
  ButtonWrapper,
  DashboardArea,
  DashboardWrapper,
  EditApiaryButton,
  PesticideMapCol,
  PesticideMapIcon,
  PesticideMapImage,
  PesticideMapSection,
  StatesCard,
  StatesCardCol,
  StatesCardsWrapper,
  StatesTitleRow,
} from 'widgets/dashboard/ui/styles.ts';
import {GeoIcon} from 'shared/ui/icons/geoIcon.tsx';
import PesticideMap from 'shared/assets/img/pesticide_map.png';
import {ArrowRight} from 'shared/ui/icons/arrowRight.tsx';
import {WeatherWidget} from '../../WeatherWidget/ui/WeatherWidget.tsx';
import HiveBodyChart from 'features/hive/ui/HiveBody/HiveBodyChart.tsx';
import {TemperatureIcon} from 'shared/ui/icons/temperatureIcon.tsx';
import {HumidityIcon} from 'shared/ui/icons/humidityIcon.tsx';
import {HiveWeightIcon} from 'shared/ui/icons/hiveWeightIcon.tsx';
import Co2Chapter from 'shared/ui/icons/chapterIcons/co2Chapter.tsx';
import {HiveBodyHappiness} from 'features/hive/ui/HiveBody/HiveBodyHappiness.tsx';
import StateIcon from 'shared/ui/icons/stateIcon.tsx';
import {HiveIcon} from 'shared/ui/icons/hiveIcon.tsx';
import BeeIcon from 'shared/ui/icons/BeeIcon.tsx';
import BreedIcon from 'shared/ui/icons/breedIcon.tsx';
import {DeviceIcon} from 'shared/ui/icons/deviceIcon.tsx';
import BatteryIcon from 'shared/ui/icons/batteryIcon.tsx';
import ErrorIcon from 'shared/ui/icons/errorIcon.tsx';
import {CalendarCalendar} from 'features/calendar';
import {useCalendarStore} from 'entities/calendar';
import {useNotificationState} from 'entities/notifications';
import {NotificationsList} from 'widgets/notifications';
import {COLORS} from 'shared/consts/colors.ts';
import {$api} from 'shared/api';
import {chartValue} from 'widgets/chart/model/types';
import {archiveApiary, restoreApiary} from 'widgets/dashboard/helpers/requests.ts';
import {useUserStore} from 'entities/user';
import {SubmitModal} from 'widgets/SubmitModal';
import {toast} from 'react-toastify';
import {HiveAudios} from 'widgets/hiveAudios/index.ts';
import {CreateApiary} from 'features/apiary/index.ts';
import {useHivesSoundsStore} from '../../../entities/hiveSound';

type ApiPoint = {time?: string; timestamp?: string; date?: string; value?: number | string | null};
type SummaryResponse = {
  temperature: ApiPoint[];
  humidity: ApiPoint[];
  co2: ApiPoint[];
  weight: ApiPoint[];
};

type ApiaryStateResponse = {
  happinessIndex: number;
  happinessIndexEarly: number;
  hiveCount: number;
  strength: number;
  breed: number;
  attentions: number;
  battery: number;
};

function toSeries(arr?: {date: string; value: number}[]): chartValue[] {
  return (arr ?? []).map((p) => ({
    x: new Date(p.date).getTime(),
    y: p.value,
  }));
}
function lastTimeStr(data?: {date: string; value: number}[]): string {
  if (!data || data.length === 0) return '';
  const lastRaw = data[data.length - 1]?.date;
  if (!lastRaw) return '';
  const last = new Date(lastRaw);
  if (Number.isNaN(last.getTime())) return '';
  const hh = String(last.getHours()).padStart(2, '0');
  const mm = String(last.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
function avgValue(data?: {date: string; value: number}[]): number {
  if (!data || data.length === 0) return 0;
  const s = data.reduce((acc, p) => acc + (p?.value ?? 0), 0);
  return Number((s / data.length).toFixed(1));
}
function avgGain(data?: {date: string; value: number}[]): number {
  if (!data || data.length < 2) return 0;
  let sumGain = 0;
  let count = 0;
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1]?.value ?? 0;
    const cur = data[i]?.value ?? 0;
    sumGain += cur - prev;
    count++;
  }
  return Number((sumGain / count).toFixed(2));
}
function toNumeric(val: string | number | null | undefined): number | null {
  if (val === null || val === undefined) return null;
  const num = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(num) ? num : null;
}

function mapApi(arr: ApiPoint[] | undefined): {date: string; value: number}[] {
  return (arr ?? [])
    .map((p) => {
      const date = p.time ?? p.timestamp ?? p.date ?? null;
      const value = toNumeric(p.value);
      if (!date || value === null) return null;
      return {date, value};
    })
    .filter(
      (p): p is {date: string; value: number} =>
        p !== null && !Number.isNaN(new Date(p.date).getTime())
    );
}
function iso24HoursAgo(): string {
  const d = new Date(Date.now() - 24 * 3600_000);
  return d.toISOString();
}
function isoNow(): string {
  return new Date().toISOString();
}

export const Dashboard = () => {
  const currentApiary = useApiariesStore((s) => s.currentApiary());
  const setPageTitle = usePageTitleStore((s) => s.setDynamicTitle);
  const setOutletColor = useOutletColorStore((s) => s.setColor);
  const events = useCalendarStore((s) => s.events);
  const notifications = useNotificationState((s) => s.notifications);
  const audioRecords = useHivesSoundsStore((s) => s.items);
  const alarms = useHivesSoundsStore((s) => s.countAlarms);
  const allDevices = useDevicesStore((s) => s.allDevices);
  const getDevicesFromServer = useDevicesStore((s) => s.getDevicesFromServer);

  const [temperature, setTemperature] = useState<{date: string; value: number}[]>([]);
  const [humidity, setHumidity] = useState<{date: string; value: number}[]>([]);
  const [co2, setCo2] = useState<{date: string; value: number}[]>([]);
  const [weight, setWeight] = useState<{date: string; value: number}[]>([]);

  const [hiveCount, setHiveCount] = useState<number>(0);
  const [strength, setStrength] = useState<number>(0);
  const [breed, setBreed] = useState<number>(0);
  const [deviceCount, setDeviceCount] = useState<number>(0);
  const [batteryLow, setBatteryLow] = useState<number>(0);
  const [happinessIndex, setHappinessIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!currentApiary) return;

    setPageTitle(currentApiary.name);
    setOutletColor(COLORS.BG.PAGE_FILLED);

    const apiaryId = String(
      currentApiary?.id ??
        (currentApiary as any)?.apiaryId ??
        (currentApiary as any)?.apiary?.id ??
        ''
    );
    if (!apiaryId) return;

    let cancelled = false;

    const run = async () => {
      try {
        const summaryHttp = await $api.get<SummaryResponse>('/collector/analytics/summary', {
          params: {
            apiaryId,
            from: iso24HoursAgo(),
            to: isoNow(),
            interval: 'hour',
          },
        });
        if (!cancelled) {
          const data = summaryHttp?.data || ({} as SummaryResponse);
          const tempData = mapApi(data.temperature);
          const humidData = mapApi(data.humidity);
          const co2Data = mapApi(data.co2);
          const weightData = mapApi(data.weight);

          console.log('[Dashboard] API response:', {
            temperature: tempData.length,
            humidity: humidData.length,
            co2: co2Data.length,
            weight: weightData.length,
          });

          setTemperature(tempData);
          setHumidity(humidData);
          setCo2(co2Data);
          setWeight(weightData);
        }
      } catch (err) {
        console.error('Dashboard summary request failed', err);
        if (!cancelled) {
          setTemperature([]);
          setHumidity([]);
          setCo2([]);
          setWeight([]);
        }
      }

      try {
        const stateHttp = await $api.get<ApiaryStateResponse>(
          `/analytics/apiaries/${encodeURIComponent(apiaryId)}/state`
        );
        if (!cancelled) {
          const s = stateHttp?.data || ({} as ApiaryStateResponse);
          setHappinessIndex(typeof s.happinessIndex === 'number' ? s.happinessIndex : null);
          setHiveCount(Number(s.hiveCount ?? 0));
          setStrength(Number(s.strength ?? 0));
          setBreed(Number(s.breed ?? 0));
          setBatteryLow(Number(s.battery ?? 0));
        }
      } catch {
        // err
      }

      try {
        await getDevicesFromServer({apiaryId});
      } catch (err) {
        console.error(err);
      }

      try {
        await getDevicesFromServer({apiaryId});
      } catch (err) {
        console.error(err);
      }
    };

    run();

    return () => {
      cancelled = true;
      setPageTitle('');
    };
  }, [currentApiary, setPageTitle, setOutletColor, getDevicesFromServer]);

  useEffect(() => {
    setDeviceCount(allDevices.length);
  }, [allDevices]);

  useEffect(() => {
    setDeviceCount(allDevices.length);
  }, [allDevices]);

  // const tempSeries = useMemo(() => toSeries(temperature), [temperature]);
  // const humidSeries = useMemo(() => toSeries(humidity), [humidity]);
  // const co2Series = useMemo(() => toSeries(co2), [co2]);
  // const weightSeries = useMemo(() => toSeries(weight), [weight]);

  const tempSeries = toSeries(temperature);
  const humidSeries = toSeries(humidity);
  const co2Series = toSeries(co2);
  const weightSeries = toSeries(weight);

  const tempTime = lastTimeStr(temperature);
  const humidTime = lastTimeStr(humidity);
  const co2Time = lastTimeStr(co2);
  const weightTime = lastTimeStr(weight);

  const tempAvg = avgValue(temperature);
  const humidAvg = avgValue(humidity);
  const co2Avg = avgValue(co2);
  const weightAvg = avgValue(weight);
  const weightGainAvg = avgGain(weight);

  const hasTemperatureData = temperature.length > 0;
  const hasHumidityData = humidity.length > 0;
  const hasCo2Data = co2.length > 0;
  const hasWeightData = weight.length > 0;

  const isDeleted = currentApiary?.deletedAt;
  const getApiaries = useApiariesStore((s) => s.getApiaries);
  const userInfo = useUserStore((s) => s.userInfo);

  const onArchive = async () => {
    try {
      let status = 404;
      if (isDeleted) {
        status = await restoreApiary(currentApiary?.id);
      } else {
        status = await archiveApiary(currentApiary?.id);
      }
      if (status >= 200 && status < 300) {
        await getApiaries(userInfo?.id);
      }
    } catch (err) {
      console.log(err);
      toast.error('Ошибка при архивации пасеки');
    } finally {
      setIsOpenArchiveModal(false);
    }
  };

  const [isOpenArchiveModal, setIsOpenArchiveModal] = useState<boolean>(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false);

  return (
    <>
      <DashboardWrapper>
        <DashboardArea $area={'state'}>
          <StatesTitleRow>
            <StateIcon />
            <h3>Состояние пасеки</h3>
          </StatesTitleRow>
          {/* Хардкод для выставки 8.10.26*/}
          <HiveBodyHappiness value={(currentApiary?.id === 'fe27e602-df77-493e-83e1-4fc4c18a7563' ? 4.3 : happinessIndex) ?? undefined} />
          <StatesCardsWrapper>
            <StatesCard>
              <HiveIcon />
              <StatesCardCol>
                <h4>{hiveCount}</h4>
                <p>Всего ульев</p>
              </StatesCardCol>
            </StatesCard>
            <StatesCard>
              <BeeIcon />
              <StatesCardCol>
                <h4>{strength}</h4>
                <p>Общая сила</p>
              </StatesCardCol>
            </StatesCard>
            <StatesCard>
              <BreedIcon />
              <StatesCardCol>
                <h4>{breed}</h4>
                <p>Общий расплод</p>
              </StatesCardCol>
            </StatesCard>
            <StatesCard>
              <DeviceIcon />
              <StatesCardCol>
                <h4>{deviceCount}</h4>
                <p>Модулей</p>
              </StatesCardCol>
            </StatesCard>
            <StatesCard>
              <BatteryIcon />
              <StatesCardCol>
                <h4>{batteryLow}</h4>
                <p>Низкий заряд</p>
              </StatesCardCol>
            </StatesCard>
            <StatesCard>
              <ErrorIcon />
              <StatesCardCol>
                <h4>{alarms}</h4>
                <p>Тревоги</p>
              </StatesCardCol>
            </StatesCard>
          </StatesCardsWrapper>
        </DashboardArea>

        <DashboardArea $area={'weight'}>
          <HiveBodyChart
            icon={<HiveWeightIcon />}
            title={'Масса улья'}
            lastUpdatedTime={hasWeightData ? weightTime : undefined}
            to={'/charts/apiaryweight'}
            type={'column'}
            data={hasWeightData ? weightSeries : []}
            tooltipText={[
              {title: 'Масса улья', dim: 'кг', isSign: false},
              {title: 'Привес', dim: 'кг', isSign: true},
            ]}
            seriesLabel="Масса улья"
            showDiff
            averages={
              hasWeightData
                ? [
                    {value: weightAvg, unit: 'кг'},
                    {value: weightGainAvg, unit: 'кг'},
                  ]
                : []
            }
          />
        </DashboardArea>

        <DashboardArea $area={'humidity'}>
          <HiveBodyChart
            icon={<HumidityIcon />}
            title={'Влажность'}
            lastUpdatedTime={hasHumidityData ? humidTime : undefined}
            to={'/charts/apiaryhumidity'}
            type={'sparkline'}
            data={hasHumidityData ? humidSeries : []}
            tooltipText={[{title: 'Влажность', dim: '%', isSign: false}]}
            seriesLabel="Влажность"
            averages={hasHumidityData ? [{value: Math.round(humidAvg), unit: '%'}] : []}
          />
        </DashboardArea>

        <DashboardArea $area={'co2'}>
          <HiveBodyChart
            icon={<Co2Chapter />}
            title={'CO₂'}
            lastUpdatedTime={hasCo2Data ? co2Time : undefined}
            to={'/charts/apiaryco2'}
            type={'sparkline'}
            data={hasCo2Data ? co2Series : []}
            tooltipText={[{title: 'CO₂', dim: 'ppm', isSign: false}]}
            seriesLabel="CO₂"
            averages={hasCo2Data ? [{value: Math.round(co2Avg), unit: 'ppm'}] : []}
          />
        </DashboardArea>

        <DashboardArea $area={'temp'}>
          <HiveBodyChart
            icon={<TemperatureIcon />}
            title={'Температура'}
            lastUpdatedTime={hasTemperatureData ? tempTime : undefined}
            to={'/charts/apiarytemperature'}
            type={'sparkline'}
            data={hasTemperatureData ? tempSeries : []}
            tooltipText={[{title: 'Температура', dim: '°C', isSign: false}]}
            seriesLabel="Температура"
            averages={hasTemperatureData ? [{value: tempAvg, unit: '°C'}] : []}
          />
        </DashboardArea>

        <DashboardArea $area={'weather'}>
          <WeatherWidget />
        </DashboardArea>

        <DashboardArea $area={'pesticide'}>
          <PesticideMapSection to={'/pesticide-map'}>
            <PesticideMapIcon>
              <GeoIcon />
            </PesticideMapIcon>
            <PesticideMapCol>
              <h3>Карта пестицидов</h3>
              <p>
                Подробнее <ArrowRight />
              </p>
            </PesticideMapCol>
            <PesticideMapImage src={PesticideMap} />
          </PesticideMapSection>
        </DashboardArea>

        <DashboardArea $area={'calendar'}>
          <CalendarCalendar
            activeDate={new Date()}
            events={events}
            isVidget
            onChangeFilters={() => null}
            onEventDrop={() => null}
            onEventResize={() => null}
          />
        </DashboardArea>
        <DashboardArea $area={'notification'}>
          <NotificationsList notifications={notifications.slice(0, 10)} isVidget />
        </DashboardArea>
        <DashboardArea $area={'sound'}>
          <HiveAudios isWidget audioRecords={audioRecords.slice(0, 3)} />
        </DashboardArea>
        <ButtonWrapper>
          <EditApiaryButton onClick={() => setIsOpenEditModal(true)}>
            Изменить пасеку
          </EditApiaryButton>
          <ArchiveApiaryButton onClick={() => setIsOpenArchiveModal(true)}>
            Перенести пасеку в архив
          </ArchiveApiaryButton>
        </ButtonWrapper>
      </DashboardWrapper>

      <CreateApiary
        isOpen={isOpenEditModal}
        onClose={() => setIsOpenEditModal(false)}
        position={'top'}
        edit
      />
      <SubmitModal
        isOpen={isOpenArchiveModal}
        onClose={() => setIsOpenArchiveModal(false)}
        onConfirm={onArchive}
        title={'Вы уверены, что хотите перенести пасеку в архив?'}
        subtitle={'Все устройства будут отключены'}
      />
    </>
  );
};
